import React, { useCallback, useEffect, useState } from "react";
import api from "../api";
import { downloadReport } from "../download";

const FIELDS = [
  ["ref_no", "Reference No"], ["project_name", "Project Name"], ["project_type", "Project Type"],
  ["department", "Department"], ["requester", "Requester"], ["processing_purpose", "Processing Purpose"],
  ["data_categories", "Data Categories"],
];
const levelClass = (l) => (/very high|high/i.test(l) ? "danger" : /medium/i.test(l) ? "warn" : "ok");
const statusFlow = { Draft: "Submitted", Submitted: "Under Review", "Under Review": "Approved" };

function Editor({ questions, initial, onClose, onSaved }) {
  const [f, setF] = useState(() => {
    const base = {}; FIELDS.forEach(([k]) => (base[k] = initial?.[k] ?? ""));
    base.description = initial?.description ?? "";
    let answers = {}; try { answers = initial?.answers ? JSON.parse(initial.answers) : {}; } catch { answers = {}; }
    return { ...base, answers };
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setAns = (id, v) => setF((p) => ({ ...p, answers: { ...p.answers, [id]: v } }));

  const score = questions.reduce((s, q) => s + (f.answers[q.id] ? q.weight : 0), 0);
  const level = score >= 85 ? "Very High" : score >= 70 ? "High" : score >= 40 ? "Medium" : "Low";

  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = { ...f, answers: JSON.stringify(f.answers) };
      if (initial?.id) await api.put(`/pia/${initial.id}`, payload); else await api.post("/pia", payload);
      onSaved();
    } catch { setSaving(false); }
  };

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <form className="modal" style={{ maxWidth: 720 }} onSubmit={submit}>
        <h3>{initial ? "Edit" : "New"} PIA / DPIA</h3>
        <div className="grid2">
          {FIELDS.map(([k, l]) => (
            <div className="form-row" key={k}><label>{l}</label><input value={f[k] ?? ""} onChange={(e) => set(k, e.target.value)} /></div>
          ))}
        </div>
        <div className="form-row"><label>Description</label><textarea value={f.description} onChange={(e) => set("description", e.target.value)} /></div>

        <div style={{ margin: "8px 0 6px", fontWeight: 700 }}>Risk Questionnaire</div>
        {questions.map((q) => (
          <label key={q.id} style={{ display: "flex", gap: 10, alignItems: "center", padding: "6px 0", borderBottom: "1px solid #f1f5f9", fontSize: 13 }}>
            <input type="checkbox" style={{ width: "auto" }} checked={!!f.answers[q.id]} onChange={(e) => setAns(q.id, e.target.checked)} />
            <span style={{ flex: 1 }}>{q.text}</span>
            <span className="pill">+{q.weight}</span>
          </label>
        ))}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 12 }}>
          <div style={{ fontSize: 13, color: "#64748b" }}>Automatic risk score</div>
          <div style={{ fontSize: 24, fontWeight: 800 }}>{score}</div>
          <span className={`pill ${levelClass(level)}`}>{level}</span>
        </div>
        <div className="foot">
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn primary" disabled={saving}>{saving ? "Saving…" : "Save"}</button>
        </div>
      </form>
    </div>
  );
}

export default function PIA() {
  const [rows, setRows] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    const res = await api.get("/pia", { params: { pageSize: 200 } });
    setRows(res.data?.data?.rows || []);
  }, []);
  useEffect(() => { api.get("/pia/questions").then((r) => setQuestions(r.data?.data || [])); }, []);
  useEffect(() => { load(); }, [load]);

  const transition = async (row, status) => {
    const note = window.prompt(`Note for "${status}" (optional):`) ?? "";
    await api.post(`/pia/${row.id}/transition`, { status, note });
    load();
  };
  const del = async (row) => { if (window.confirm("Delete this PIA?")) { await api.delete(`/pia/${row.id}`); load(); } };

  return (
    <div>
      <h1>Privacy Impact Assessment (PIA / DPIA)</h1>
      <p className="sub">Assess privacy risk before launching new projects, products, vendors or AI.</p>
      <div className="card">
        <div className="toolbar">
          <div style={{ flex: 1 }} />
          <button className="btn sm" onClick={() => downloadReport("pia", "xlsx")}>Excel</button>
          <button className="btn sm" onClick={() => downloadReport("pia", "pdf")}>PDF</button>
          <button className="btn primary" onClick={() => setEditing("new")}>+ New Assessment</button>
        </div>
        <div className="tablewrap">
          <table>
            <thead><tr><th>Ref</th><th>Project</th><th>Type</th><th>Department</th><th>Score</th><th>Level</th><th>Status</th><th style={{ width: 240 }}></th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>{r.ref_no || "—"}</td><td>{r.project_name}</td><td>{r.project_type || "—"}</td><td>{r.department || "—"}</td>
                  <td><b>{r.risk_score}</b></td><td><span className={`pill ${levelClass(r.risk_level)}`}>{r.risk_level}</span></td>
                  <td><span className="pill">{r.status}</span></td>
                  <td>
                    <button className="btn link sm" onClick={() => setEditing(r)}>Edit</button>
                    {statusFlow[r.status] && <button className="btn link sm" onClick={() => transition(r, statusFlow[r.status])}>→ {statusFlow[r.status]}</button>}
                    {r.status === "Under Review" && <button className="btn link sm danger" onClick={() => transition(r, "Rejected")}>Reject</button>}
                    <button className="btn link sm danger" onClick={() => del(r)}>Delete</button>
                  </td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={8}><div className="empty">No assessments yet.</div></td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      {editing && <Editor questions={questions} initial={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}
